import { Sparkles, TrendingUp, Calendar, Wallet } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { cn } from '@/lib/utils'

interface DashboardProjectionSectionProps {
  confirmedRevenue: number
  expectedRevenue: number
  futureRecurringRevenue: number
  projectedRevenue: number
  dailyAverage: number
  daysRemaining: number
  isOnTrack: boolean
  projectionMessage: string
  monthlyGoal: number
}

const formatCurrency = (value: number) => 
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

export function DashboardProjectionSection({
  confirmedRevenue,
  expectedRevenue,
  futureRecurringRevenue,
  projectedRevenue,
  dailyAverage,
  daysRemaining,
  isOnTrack,
  projectionMessage,
  monthlyGoal
}: DashboardProjectionSectionProps) {
  const gap = Math.max(0, monthlyGoal - projectedRevenue)

  return (
    <section>
      <Card className="shadow-card border-primary/20 bg-gradient-to-br from-primary/5 via-transparent to-transparent">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Projeção do Mês
          </CardTitle>
          <CardDescription>
            Baseada no seu ritmo atual e pagamentos previstos
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Revenue breakdown */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {/* Confirmed */}
            <div className="p-4 bg-card rounded-xl border shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 rounded-lg bg-success/10">
                  <Wallet className="h-4 w-4 text-success" />
                </div>
                <span className="text-xs font-medium text-muted-foreground">Confirmado</span>
              </div>
              <p className="text-2xl font-bold text-success">{formatCurrency(confirmedRevenue)}</p>
            </div>

            {/* Expected (pending) */}
            <div className="p-4 bg-card rounded-xl border shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 rounded-lg bg-warning/10">
                  <Calendar className="h-4 w-4 text-warning" />
                </div>
                <span className="text-xs font-medium text-muted-foreground">Previsto</span>
              </div>
              <p className="text-2xl font-bold text-warning">{formatCurrency(expectedRevenue)}</p>
            </div>

            {/* Future recurring */}
            <div className="p-4 bg-card rounded-xl border shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 rounded-lg bg-primary/10">
                  <Sparkles className="h-4 w-4 text-primary" />
                </div>
                <span className="text-xs font-medium text-muted-foreground">Recorrente</span>
              </div>
              <p className="text-2xl font-bold">{formatCurrency(futureRecurringRevenue)}</p>
            </div>

            {/* Projected total */}
            <div className={cn(
              "p-4 rounded-xl border shadow-sm",
              isOnTrack 
                ? "bg-gradient-to-br from-success/10 to-success/5 border-success/20" 
                : "bg-gradient-to-br from-warning/10 to-warning/5 border-warning/20"
            )}>
              <div className="flex items-center gap-2 mb-2">
                <div className={cn(
                  "p-1.5 rounded-lg",
                  isOnTrack ? "bg-success/10" : "bg-warning/10"
                )}>
                  <TrendingUp className={cn("h-4 w-4", isOnTrack ? "text-success" : "text-warning")} />
                </div>
                <span className="text-xs font-medium text-muted-foreground">Projeção Final</span>
              </div>
              <p className={cn(
                "text-2xl font-bold",
                isOnTrack ? "text-success" : "text-warning"
              )}>
                {formatCurrency(projectedRevenue)}
              </p>
            </div>
          </div>

          {/* Projection message */}
          <div className={cn(
            "p-4 rounded-xl border text-center",
            isOnTrack 
              ? "bg-success/5 border-success/20" 
              : "bg-warning/5 border-warning/20"
          )}>
            <p className={cn(
              "text-sm font-medium",
              isOnTrack ? "text-success" : "text-warning"
            )}>
              {projectionMessage}
            </p>
          </div>

          {/* Additional metrics */}
          <div className="flex items-center justify-center gap-8 pt-2 border-t text-sm">
            <div className="text-center">
              <p className="text-muted-foreground">Média diária</p>
              <p className="font-semibold text-foreground">{formatCurrency(dailyAverage)}</p>
            </div>
            <div className="h-8 w-px bg-border" />
            <div className="text-center">
              <p className="text-muted-foreground">Dias restantes</p>
              <p className="font-semibold text-foreground">{daysRemaining}</p>
            </div>
            {!isOnTrack && (
              <>
                <div className="h-8 w-px bg-border" />
                <div className="text-center">
                  <p className="text-muted-foreground">Gap para meta</p>
                  <p className="font-semibold text-destructive">{formatCurrency(gap)}</p>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </section>
  )
}
